import React, { useState, useEffect } from "react";
import { Row, Col, Button } from "antd";
import { Link } from "react-router-dom";
import {
  FacebookOutlined,
  TwitterOutlined,
  InstagramOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import styled from "styled-components";

const FooterWrapper = styled.footer`
  background: #1a1a1a;
  color: #fff;

  .footer-main {
    padding: 60px 0 30px;

    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
    }

    .footer-section {
      h3 {
        color: #fff;
        font-size: 1.2rem;
        margin-bottom: 20px;
        font-weight: bold;
      }

      ul {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          margin-bottom: 10px;

          a {
            color: #ccc;
            text-decoration: none;
            transition: color 0.3s;

            &:hover {
              color: #fd8e07;
            }
          }
        }
      }

      p {
        color: #ccc;
        line-height: 1.6;
      }
    }

    .social-links {
      display: flex;
      gap: 15px;
      margin-top: 20px;

      .social-btn {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: #333;
        border: none;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s;

        &:hover {
          background: #667eea;
          transform: translateY(-2px);
        }
      }
    }

    .payment-methods {
      margin-top: 20px;

      img {
        height: 30px;
        margin-right: 10px;
        opacity: 0.7;
        transition: opacity 0.3s;

        &:hover {
          opacity: 1;
        }
      }
    }
  }

  .footer-bottom {
    border-top: 1px solid #333;
    padding: 20px 0;
    text-align: center;

    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      @media (max-width: 768px) {
        flex-direction: column;
        gap: 10px;
      }
    }

    .copyright {
      color: #999;
      font-size: 14px;
    }

    .footer-links {
      display: flex;
      gap: 20px;

      @media (max-width: 768px) {
        flex-wrap: wrap;
        justify-content: center;
      }

      a {
        color: #999;
        text-decoration: none;
        font-size: 14px;

        &:hover {
          color: #fff;
        }
      }
    }
  }
`;

const Footer = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const companyLinks = [""];

  const supportLinks = [
    { label: "Privacy Policy", path: "/privacy-policy" },
    { label: "Terms & Conditions", path: "/terms-and-conditions" },
    { label: "Return & Refund Policy", path: "/return-refund" },
  ];

  const legalLinks = [
    { label: "Privacy Policy", path: "/privacy-policy" },
    { label: "Terms of Service", path: "/terms-and-conditions" },
    { label: "Return & Refund", path: "/return-refund" },
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/categories`;
      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }

      const data = await response.json();

      if (data.status && data.data) {
        const topLevelCategories = data.data;
        setCategories(topLevelCategories);
      }

      setError(null);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError(err.message);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FooterWrapper>
      <div className="footer-main">
        <div className="container">
          <Row gutter={[32, 32]}>
            <Col xs={24} sm={12} md={6}>
              <div className="footer-section">
                <h3>GenZ Wearables</h3>
                <p>
                  Your premier destination for quality fashion and accessories.
                  Discover the latest trends and timeless classics for every
                  style.
                </p>
              </div>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div className="footer-section">
                <h3>Products</h3>
                <ul>
                  {loading ? (
                    <li style={{ color: "#999" }}>Loading...</li>
                  ) : error ? (
                    <li style={{ color: "#999" }}>Error loading categories</li>
                  ) : categories && categories.length > 0 ? (
                    categories.slice(0, 4).map((category, index) => (
                      <li key={category.id || index}>
                        <Link
                          to={`/clothes/${category.slug || category.name}`}
                          style={{ textTransform: "capitalize" }}
                        >
                          {category.name}
                        </Link>
                      </li>
                    ))
                  ) : (
                    <li style={{ color: "#999" }}>No categories available</li>
                  )}
                </ul>
              </div>
            </Col>
            {/* 
            <Col xs={24} sm={12} md={6}>
              <div className="footer-section">
                <h3>Our Company</h3>
                <ul>
                  {companyLinks.map((link, index) => (
                    <li key={index}>
                      <a href="#">{link}</a>
                    </li>
                  ))}
                </ul>
              </div>
            </Col> */}

            <Col xs={24} sm={12} md={6}>
              <div className="footer-section">
                <h3>Customer Support</h3>
                <ul>
                  {supportLinks.map((link, index) => (
                    <li key={index}>
                      <Link to={link.path}>{link.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div className="footer-section">
                <h3>Follow Us</h3>
                <div className="social-links">
                  <Button className="social-btn" icon={<FacebookOutlined />} />
                  <Button className="social-btn" icon={<TwitterOutlined />} />
                  <Button className="social-btn" icon={<InstagramOutlined />} />
                  <Button className="social-btn" icon={<YoutubeOutlined />} />
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container">
          <div className="copyright">
            © 2026 - Demo Store. All rights reserved.
          </div>
          <div className="footer-links">
            {legalLinks.map((link, index) => (
              <Link key={index} to={link.path}>
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </FooterWrapper>
  );
};

export default Footer;
